import { ComputedSerie } from "@nivo/line";
import { useTooltip } from "@nivo/tooltip";
import { line } from "d3-shape";
import { createElement, FunctionComponent, useCallback, useMemo } from "react";
import { LineData } from "src/components/monitoring/senec-state-history-chart/stateHistoryTypes";
import { createPortal, flushSync } from "react-dom";

interface MaxChartValues {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
}

export const LineWithTooltip = ({
    serie,
    tooltip,
    marginLeft,
    marginTop,
    onLineClick,
    maxValues,
}: {
    serie: ComputedSerie;
    tooltip: FunctionComponent<LineData>;
    marginLeft: number;
    marginTop: number;
    onLineClick: (data: LineData) => void;
    maxValues: MaxChartValues;
}) => {
    const { id, data, color, tooltipText } = serie;
    const { showTooltipAt, hideTooltip } = useTooltip();
    const lineGenerator = useMemo(
        () =>
            line()
                .x((d) => d[0])
                .y((d) => d[1]),
        [],
    );

    const tooltipPoint: [number, number] = useMemo(() => {
        if (data.length < 2) {
            throw new Error("Data must contain at least two elements");
        }

        const totalX = data.reduce((sum, { position: { x } }) => sum + x, 0);
        const averageX = totalX / data.length;
        const { y } = data[1].position;
        const [initialX, initialY] = [averageX + marginLeft, y + marginTop];
        const adjustedTooltipPoint = [initialX, initialY];

        return [adjustedTooltipPoint[0], adjustedTooltipPoint[1]];
    }, [data, marginLeft, marginTop, maxValues]);

    const tooltipProps: LineData = useMemo(
        () => ({
            tooltipText,
            x: tooltipPoint[0],
            y: tooltipPoint[1],
            color,
            entry: serie.entry,
        }),
        [tooltipText, tooltipPoint, serie],
    );

    const onMouseEnter = useCallback(() => {
        const tooltipPortal = document.getElementById("tooltip-portal")! as HTMLDivElement;
        const [x, y] = tooltipPoint;
        // Ensure everything is rendered to the DOM to set styles later
        flushSync(() => {
            showTooltipAt(createPortal(createElement(tooltip, tooltipProps), tooltipPortal), [x, y], "center");
        });
        const tooltipElement = tooltipPortal.children.item(0) as HTMLDivElement | null;
        if (tooltipElement) {
            tooltipElement.style.left = `${x}px`;
            tooltipElement.style.top = `${y}px`;
        }
    }, [tooltip, showTooltipAt, tooltipPoint]);

    const onMouseLeave = () => {
        hideTooltip();
    };

    const onClick = () => {
        onLineClick(tooltipProps);
    };
    const path = lineGenerator(data.map((p) => [p.position.x!, p.position.y!]));
    return (
        <path
            data-testid={`chart-line-${serie.id}`}
            key={id}
            id={id.toString()}
            d={path!}
            fill="none"
            strokeWidth={10}
            stroke={color}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={onClick}
            style={{ cursor: "pointer" }}
        />
    );
};
